/**
 * Format a price value to display in CLP format
 * @param {number|string} price - The price to format
 * @returns {string} Formatted price without decimal places
 */
export const formatPrice = (price) => {
  // Handle null, undefined or NaN values
  if (price === null || price === undefined || isNaN(Number(price))) {
    console.warn('Invalid price value:', price);
    return "0";
  }
  
  try {
    // Convert price to number if it's a string
    const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
    
    // Ensure we have a valid number after conversion
    if (isNaN(numericPrice)) {
      console.warn('Price conversion resulted in NaN:', price);
      return "0";
    }
    
    // Round to integer (no decimal places for CLP)
    const roundedPrice = Math.round(numericPrice);
    
    // Format with thousand separators
    return roundedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  } catch (error) {
    console.error('Error in formatPrice:', error);
    return "0";
  }
};

/**
 * Calculate discounted price
 * @param {number} originalPrice - The original price
 * @param {number} discountPercentage - The discount percentage
 * @returns {number} The price after discount
 */
export const calculateDiscountedPrice = (originalPrice, discountPercentage) => {
  if (!originalPrice || isNaN(originalPrice) || originalPrice <= 0) return 0;
  if (!discountPercentage || isNaN(discountPercentage) || discountPercentage <= 0) return originalPrice;
  
  const discount = (originalPrice * discountPercentage) / 100;
  return originalPrice - discount;
};

export const getMinPrice = (blisters) => {
  if (blisters && blisters.length > 0) {
    return Math.min(...blisters.map(blister => parseFloat(blister.price)));
  }
  return 0;
};

export const getMaxPrice = (blisters) => {
  if (blisters && blisters.length > 0) {
    return Math.max(...blisters.map(blister => parseFloat(blister.price)));
  }
  return 0;
};

/**
 * Get the min and max price for a product with variants
 * @param {Object} product - The product object
 * @returns {Object} Object with min and max prices
 */
export const getProductPriceRange = (product) => {
  if (!product) return { min: 0, max: 0 };
  
  // Base price (default)
  const basePrice = Number(product.price_sparta || product.price || 0);
  
  // If no variants, just return the base price as both min and max
  if (!product.has_variants || !product.variants || !product.variants.length) {
    return { min: basePrice, max: basePrice };
  }
  
  // Start with base price for both min and max
  let minPrice = basePrice;
  let maxPrice = basePrice;
  
  // Check all variant options for price adjustments
  product.variants.forEach(variant => {
    if (variant.options && Array.isArray(variant.options)) {
      variant.options.forEach(option => {
        if (option.price_adjustment) {
          const adjustedPrice = basePrice + Number(option.price_adjustment);
          if (adjustedPrice < minPrice) minPrice = adjustedPrice;
          if (adjustedPrice > maxPrice) maxPrice = adjustedPrice;
        }
      });
    }
  });
  
  return { min: minPrice, max: maxPrice };
};